import React, { Component } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import classnames from "classnames";
import { push } from "store/actions/router";
import { employerSite, individualSite } from "utils/determineSite";
import { Collapse } from "react-bootstrap";
import { NavLink, useLocation } from "react-router-dom";
import type { Location } from "react-router-dom";
import { get, includes } from "lodash";
import { lifecycleStates } from "statics/states";
import { sidebarHide } from "actions/sidebarActions";
import { Auth0Context } from "utils/react-auth0-wrapper";
import { FiCreditCard, FiFileText, FiLogOut } from "react-icons/fi";
import { billingVerificationPending } from "store/selectors/employer";
import type { RootState } from "types/state";

import "./SideBar.scss";

/**
 * Shape of the route-config objects SideBar iterates over. They originate in
 * `pages/dashboards/Routes`; only the fields this component reads are modeled.
 * `collapse` groups carry nested `views`; the rest are leaf links.
 */
interface SideBarRoute {
  path: string;
  name?: string;
  icon?: React.ReactNode;
  state?: string;
  collapse?: boolean;
  views?: SideBarRoute[];
  redirect?: boolean;
  layout?: string;
  hidden?: boolean;
}

interface SideBarOwnProps {
  routes: SideBarRoute[];
  color?: string;
  image?: string;
  isEmployer?: boolean;
  isSmallScreen?: boolean;
}

const mapStateToProps = (state: RootState) => {
  // `state.user.userState` is the `object`-typed slice, so this deep get is
  // `unknown`; it resolves to the user-state string at runtime — cast at the
  // boundary like the other selectors.
  const userState = get(state.user, "userState.state", "") as string;
  const billingPortalURL = billingVerificationPending(state)
    ? null
    : // `billingPortalURL` is `unknown` on the slice; it's a URL string when
      // present — cast at the boundary.
      (get(state.employer, "billingPortalURL", null) as string | null);

  return {
    showSidebar: state.sidebar.showSidebar,
    hasVirtualBank: get(state.employer, "hasVirtualBank"),
    billingPortalURL,
    isIndividualDashboard:
      lifecycleStates.includes(userState) && individualSite(),
  };
};

const mapDispatchToprops = {
  push,
  sidebarHide,
};

const connector = connect(mapStateToProps, mapDispatchToprops);

type PropsFromRedux = ConnectedProps<typeof connector>;

type SideBarProps = PropsFromRedux & SideBarOwnProps;

// `SideBar_WithLocation` injects `location` (from useLocation) on top of the
// connected/withApollo props, so the class sees it in addition to SideBarProps.
type SideBarClassProps = WithApolloClient<SideBarProps> & {
  location: Location;
};

// Collapse groups track open/closed state keyed by `route.state`; the value is
// a boolean. Built dynamically from the route tree, so the index signature.
type SideBarState = Record<string, boolean>;

export class SideBar extends Component<SideBarClassProps, SideBarState> {
  static contextType = Auth0Context;
  // static contextType alone doesn't type this.context — declare it from the
  // (now-typed) Auth0Context so this.context is Auth0ContextValue | undefined.
  declare context: React.ContextType<typeof Auth0Context>;

  // ref callback target assigned in render; never read back.
  sidebarWrapper: HTMLDivElement | null = null;

  constructor(props: SideBarClassProps) {
    super(props);

    this.state = {
      ...this.getCollapseStates(props.routes),
    };
  }

  // this creates the initial state of this component based on the collapse routes
  // that it gets through this.props.routes
  getCollapseStates = (routes: SideBarRoute[]) => {
    let initialState: SideBarState = {};
    routes.map((prop) => {
      if (prop.collapse) {
        initialState = {
          [prop.state as string]: this.getCollapseInitialState(
            prop.views as SideBarRoute[]
          ),
          ...this.getCollapseStates(prop.views as SideBarRoute[]),
          ...initialState,
        };
      }
      return null;
    });
    return initialState;
  };

  // this verifies if any of the collapses should be default opened on a rerender of this component
  // for example, on the refresh of the page,
  // while on the src/views/forms/RegularForms.jsx - route /admin/regular-forms
  getCollapseInitialState(routes: SideBarRoute[]) {
    for (let i = 0; i < routes.length; i++) {
      if (
        routes[i].collapse &&
        this.getCollapseInitialState(routes[i].views as SideBarRoute[])
      ) {
        return true;
      } else if (window.location.href.indexOf(routes[i].path) !== -1) {
        return true;
      }
    }
    return false;
  }

  // this function creates the links and collapses that appear in the sidebar (left menu)
  createLinks = (routes: SideBarRoute[]) => {
    return routes.map((prop, key) => {
      const hideVirtual =
        prop.path === "/dashboard/company/bank" && this.props.hasVirtualBank;
      if (
        prop.redirect ||
        prop.layout === "/workflow" ||
        prop.hidden ||
        hideVirtual
      ) {
        return null;
      }
      if (prop.collapse) {
        const st: SideBarState = {};
        st[prop["state"] as string] = !this.state[prop.state as string];
        return (
          <li
            className={
              this.getCollapseInitialState(prop.views as SideBarRoute[])
                ? "active"
                : ""
            }
            key={key}
          >
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                this.setState(st);
              }}
            >
              <div className="sidebar-routes">
                <span className="sidebar-icon"> {prop.icon}</span>
                <p className="sidebar-name"> {prop.name}</p>

                <b
                  className={
                    this.state[prop.state as string]
                      ? "caret rotate-180"
                      : "caret"
                  }
                />
              </div>
            </a>
            <Collapse in={this.state[prop.state as string]}>
              <ul className="sidebar-nav collapsing-nav">
                {this.createLinks(prop.views as SideBarRoute[])}
              </ul>
            </Collapse>
          </li>
        );
      }
      return (
        // activeRoute can return the number 0; className accepts string only, so
        // cast to preserve the original (falsy-but-numeric) runtime value.
        <li className={this.activeRoute(prop.path) as string} key={key}>
          <NavLink
            to={prop.path}
            className={({ isActive }) =>
              isActive ? "icon-nav-link active" : "icon-nav-link"
            }
            onClick={(e) => {
              if (this.props.location.pathname === prop.path) {
                e.preventDefault();
              }
              this.props.sidebarHide();
            }}
          >
            <>
              <div className="sidebar-routes">
                <span className="sidebar-icon"> {prop.icon}</span>
                <p className="sidebar-name"> {prop.name}</p>
              </div>
            </>
          </NavLink>
        </li>
      );
    });
  };

  // verifies if routeName is the one active (in browser input)
  activeRoute = (routeName: string) => {
    if (routeName === this.props.location.pathname) {
      return this.props.location.pathname.indexOf(routeName) > -1
        ? "active"
        : "";
    } else {
      if (
        this.props.location.pathname === "/dashboard/company/bank" &&
        routeName === "/dashboard/company"
      ) {
        return "active";
      }
      if (
        this.props.location.pathname === "/dashboard/company/group" &&
        routeName === "/dashboard/company"
      ) {
        return "active";
      }
      if (
        this.props.location.pathname === "/dashboard/company/info" &&
        routeName === "/dashboard/company"
      ) {
        return "active";
      }
      if (
        includes(this.props.location.pathname, "approvals") &&
        includes(routeName, "approvals")
      ) {
        return "active";
      }
      if (
        includes(this.props.location.pathname, "one-time") &&
        includes(routeName, "one-time")
      ) {
        return "active";
      }
      if (
        includes(this.props.location.pathname, "employers") &&
        includes(routeName, "employers")
      ) {
        return "active";
      }
      if (
        includes(this.props.location.pathname, "documents") &&
        includes(routeName, "documents")
      ) {
        return "active";
      }
      if (
        includes(this.props.location.pathname, "/dashboard/support") &&
        includes(routeName, "/dashboard/support")
      ) {
        return "active";
      }

      return 0;
    }
  };

  logout = () => {
    window.analytics.track("Logged Out");
    // Segment's `reset` isn't in the globals.d.ts analytics shim; cast through
    // unknown at the boundary to reach the runtime method rather than widening
    // the global type.
    (window.analytics as unknown as { reset: () => void }).reset();

    // the provider always supplies a value; assert non-null to reach logout.
    this.context!.logout({
      logoutParams: {
        returnTo: window.location.origin + "/login",
      },
    });
  };

  render() {
    return (
      <div
        className={classnames("sidebar", {
          ["show-sidebar"]: this.props.showSidebar,
        })}
      >
        <div
          className="sidebar-wrapper"
          // block body so the callback returns void: a ref callback's return
          // value (other than a cleanup fn) was always discarded by React, so
          // this is behavior-identical to the original `(ref) => (... = ref)`.
          ref={(ref) => {
            this.sidebarWrapper = ref;
          }}
        >
          <ul className="sidebar-nav">
            {/*
              here we render the links in the sidebar if the link is simple,
              we make a simple link, if not, we have to create a collapsible group,
              with the speciffic parent button and with it's children which are the links
            */}
            {this.createLinks(this.props.routes)}

            {this.props.isSmallScreen && (
              <li>
                {this.props.billingPortalURL && (
                  <a
                    // `eventKey` is a react-bootstrap Nav prop, inert on a plain
                    // <a>; preserved as a no-op rather than removed.
                    // @ts-expect-error eventKey is not a valid <a> attribute
                    eventKey="4.3"
                    href={this.props.billingPortalURL}
                    target={"_blank"}
                    rel="noopener noreferrer"
                  >
                    <div className="sidebar-routes">
                      <span className="sidebar-icon">
                        <FiCreditCard />
                      </span>
                      <p className="sidebar-name">Billing</p>
                    </div>
                  </a>
                )}
                {employerSite() && (
                  <div
                    className="icon-nav-link"
                    // `activeClassName` is inert on a plain <div>; preserved as a
                    // no-op rather than removed.
                    // @ts-expect-error activeClassName is not a valid <div> attribute
                    activeClassName="active"
                    onClick={() => this.props.push("/dashboard/documents")}
                  >
                    <div className="sidebar-logout">
                      <div className="sidebar-routes">
                        <span className="sidebar-icon">
                          <FiFileText />
                        </span>
                        <p className="sidebar-name"> Documents</p>
                      </div>
                    </div>
                  </div>
                )}

                <div
                  className="icon-nav-link"
                  // `activeClassName` is inert on a plain <div>; preserved as a
                  // no-op rather than removed.
                  // @ts-expect-error activeClassName is not a valid <div> attribute
                  activeClassName="active"
                  onClick={this.logout}
                >
                  <div className="sidebar-logout">
                    <div className="sidebar-routes">
                      <span className="sidebar-icon">
                        {!this.props.isIndividualDashboard && <FiLogOut />}
                      </span>
                      <p className="sidebar-name"> Logout</p>
                    </div>
                  </div>
                </div>
              </li>
            )}
          </ul>
        </div>
      </div>
    );
  }
}

const SideBar_WithLocation = (props: WithApolloClient<SideBarProps>) => {
  const location = useLocation();
  return <SideBar {...props} location={location} />;
};

export default connector(withApollo<SideBarProps>(SideBar_WithLocation));
